import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  NativeSelect,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import InputBase from '@mui/material/InputBase';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ListAltIcon from '@mui/icons-material/ListAlt';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VerifiedIcon from '@mui/icons-material/Verified';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const DATE_PRESETS = [
  {
    label: 'Yesterday',
    value: 'yesterday',
    getRange: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      d.setHours(0, 0, 0, 0);
      return { start: d, end: new Date(d) };
    },
  },
  {
    label: 'Last 7 Days',
    value: 'last7',
    getRange: () => {
      const end = new Date();
      end.setDate(end.getDate() - 1);
      end.setHours(0, 0, 0, 0);
      const start = new Date(end);
      start.setDate(start.getDate() - 6);
      return { start, end };
    },
  },
  {
    label: 'This Week',
    value: 'thisWeek',
    getRange: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1; // Monday = 0 offset
      const start = new Date(today);
      start.setDate(today.getDate() - diff);
      return { start, end: new Date(today) };
    },
  },
  {
    label: 'Last Week',
    value: 'lastWeek',
    getRange: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1;
      const thisMonday = new Date(today);
      thisMonday.setDate(today.getDate() - diff);
      const start = new Date(thisMonday);
      start.setDate(thisMonday.getDate() - 7);
      const end = new Date(thisMonday);
      end.setDate(thisMonday.getDate() - 1);
      return { start, end };
    },
  },
  {
    label: 'This Month',
    value: 'thisMonth',
    getRange: () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start, end: new Date(today) };
    },
  },
  {
    label: 'Last Month',
    value: 'lastMonth',
    getRange: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      const end = new Date(today.getFullYear(), today.getMonth(), 0);
      end.setHours(0, 0, 0, 0);
      return { start, end };
    },
  },
];

const CompactInput = styled(InputBase)(({ theme }) => ({
  '& .MuiInputBase-input': {
    borderRadius: 6,
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #dde1e6',
    fontSize: 13,
    padding: '6px 10px',
    fontFamily: 'Lato, sans-serif',
    '&:focus': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

const FilterBar = ({
  stores,
  selectedStore,
  setSelectedStore,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  onLoad,
  onOpenExceptionsLog,
  onOpenManageSources,
  activeView,
  onViewChange,
}) => {
  const [quickSelect, setQuickSelect] = useState('yesterday');

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      onViewChange(newView);
    }
  };

  const handleQuickSelect = (e) => {
    const value = e.target.value;
    const preset = DATE_PRESETS.find((p) => p.value === value);
    if (preset) {
      const { start, end } = preset.getRange();
      setStartDate(start);
      setEndDate(end);
    }
    setQuickSelect('');
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        {/* Title group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <VerifiedIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: 'Lato', lineHeight: 1.2 }}
            >
              Data Integrity 2.0
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', lineHeight: 1 }}
            >
              Pipeline health monitor
            </Typography>
          </Box>
          {onOpenExceptionsLog && (
            <Tooltip title={activeView === 'sources' ? 'Exceptions Log' : 'Exceptions Log (Sources only)'}>
              <span>
                <IconButton
                  size="small"
                  onClick={onOpenExceptionsLog}
                  disabled={activeView !== 'sources'}
                >
                  <ListAltIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onOpenManageSources && (
            <Tooltip title={activeView === 'sources' ? 'Manage Data Sources' : 'Add Automation'}>
              <IconButton size="small" onClick={onOpenManageSources}>
                {activeView === 'sources' ? (
                  <StorageIcon fontSize="small" />
                ) : (
                  <ScheduleIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* View Toggle */}
        <ToggleButtonGroup
          value={activeView}
          exclusive
          onChange={handleViewChange}
          size="small"
          sx={{ ml: 2 }}
        >
          <ToggleButton
            value="sources"
            sx={{
              textTransform: 'none',
              fontFamily: 'Lato',
              px: 2,
              py: 0.5,
              fontSize: 13,
            }}
          >
            <StorageIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Data Sources
          </ToggleButton>
          <ToggleButton
            value="automations"
            sx={{
              textTransform: 'none',
              fontFamily: 'Lato',
              px: 2,
              py: 0.5,
              fontSize: 13,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 18, mr: 0.5 }} />
            Automations
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Controls group - only show for sources view */}
        {activeView === 'sources' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <NativeSelect
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              input={<CompactInput />}
            >
              {stores.map(({ storeId, storeName, storeType }) => (
                <option key={storeId} value={storeId}>
                  {storeName}{storeType && storeId !== 'all' ? ` (${storeType})` : ''}
                </option>
              ))}
            </NativeSelect>

            <NativeSelect
              value={quickSelect}
              onChange={handleQuickSelect}
              input={<CompactInput />}
            >
              <option value="" disabled>
                Quick select…
              </option>
              {DATE_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </NativeSelect>

            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="Start"
              className="di2-datepicker"
            />

            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              placeholderText="End"
              className="di2-datepicker"
            />

            <Button
              variant="contained"
              color="primary"
              onClick={onLoad}
              startIcon={<PlayArrowIcon />}
              sx={{
                fontFamily: 'Lato',
                textTransform: 'none',
                px: 2,
                py: '5px',
                fontSize: 13,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              Load
            </Button>
          </Box>
        )}
      </Box>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};

export default FilterBar;
